import LexicalParser from 'app/services/formula/parser/lexicalParser';
import _ from 'lodash';

import {
  ARRAY_FUNCTION_ENUM,
  ARRAY_FUNCTIONS_WITH_ITERATORS,
  getLogicalInfixOperatorError,
  LEX_TOKEN_TYPES,
  LOGICAL_INFIX_OPERATORS,
  NUMERIC_TYPES,
  SWITCH_FUNCTION_ENUM,
} from 'constants/parser';
import { getTokenPriority } from 'utils/expressions';

import { getError } from './errorHandling';
import {
  findRootTokenIndex,
  findTokenByType,
  getIteratorToken,
  getPathToRootArray,
  minimizeTokens,
  separateTokens,
  validateTree,
} from './parserUtils';

/**
 * Top-level infix boolean chaining (`a && b`, `a || b`) is not representable
 * in the CriteriaExpressionItem grammar — boolean composition is the AND(...) /
 * OR(...) functions. Neither parser path binds `&&` / `||`, so without this
 * guard every operand after the first is silently dropped (the parse stops at
 * the first clause) yet validation still reports "0 problems" — silent data
 * loss in a saved rule (DEV-4374). Return a clear error instead.
 */
const findLogicalInfixOperatorError = tokens => {
  const token = tokens.find(
    ({ type, value }) =>
      type === LEX_TOKEN_TYPES.operator && Object.hasOwn(LOGICAL_INFIX_OPERATORS, value),
  );

  if (!token) {
    return null;
  }

  return { message: getLogicalInfixOperatorError(token.value), position: token.position };
};

// legacy under FT
export const syntaxParser = ({
  tokens,
  schemaParams,
  isMinimized,
  isOnlyBooleanAllowed,
  isOnlyNumericAllowed,
}) => {
  let error = null;

  if (!tokens.length) {
    return {};
  }

  const logicalInfixError = findLogicalInfixOperatorError(tokens);

  if (logicalInfixError) {
    return { data: {}, fields: undefined, error: logicalInfixError };
  }

  const processTokens = tokens => {
    const processedTokens = [];
    let index = 0;

    while (index < tokens.length) {
      if (
        tokens[index]?.value !== '-' ||
        (tokens[index]?.value === '-' && NUMERIC_TYPES.includes(tokens[index - 1]?.typeOfValue)) ||
        !tokens[index + 1]
      ) {
        processedTokens.push(tokens[index]);
      } else {
        index += 1;
        tokens[index].value = `-${tokens[index].value}`;
        processedTokens.push(tokens[index]);
      }
      index += 1;
    }

    return processedTokens;
  };

  const createTree = (tokens, parent) => {
    let rootIndex = findRootTokenIndex(tokens);

    // Condition for entities (Program.name, ...)
    if (rootIndex >= 0 && tokens[rootIndex].type !== 'function') {
      const root = tokens[rootIndex];
      root.operands = [
        createTree(tokens.slice(0, rootIndex), root),
        createTree(tokens.slice(rootIndex + 1), root),
      ];

      const childToken = tokens[rootIndex + 2];
      const lastToken = tokens.findLast(token => token.type !== LEX_TOKEN_TYPES.bracket);

      if (
        !error &&
        ![LEX_TOKEN_TYPES.function, LEX_TOKEN_TYPES.value, LEX_TOKEN_TYPES.field].includes(
          tokens[rootIndex + 1].type,
        ) &&
        childToken
      ) {
        error = getError(childToken, root);
      }

      if (!error && !isOnlyBooleanAllowed && !lastToken.parent) {
        error = getError(lastToken, root);
      }

      return root;
    }

    const wrongToken = tokens.find(token => LEX_TOKEN_TYPES.anySingleCharacter === token?.type);

    // Throw error if there is anySingleCharacter
    if (!error && wrongToken) {
      error = getError(wrongToken, parent);
    }

    const root = tokens[0];

    if (!root) {
      if (!error) {
        error = getError(root, parent);
      }

      return null;
    }

    if (parent) {
      root.parent = _.omit(parent, 'operands');
    }

    if ([LEX_TOKEN_TYPES.function].includes(root.type)) {
      const endIndex = findTokenByType(tokens, LEX_TOKEN_TYPES.bracket);
      const childTokens = tokens.slice(2, endIndex === -1 ? tokens.length : endIndex);
      const restTokens = tokens.slice(endIndex + 1);

      root.operands = [];

      if (schemaParams.functions[root.value].argumentsCount !== 0) {
        if (!error && childTokens.length === 0) {
          error = getError(root, parent);
        }

        let functionTokens = separateTokens(childTokens);

        if (ARRAY_FUNCTIONS_WITH_ITERATORS.includes(root.value) && !_.isEmpty(functionTokens[0])) {
          const [array, ...rest] = separateTokens(childTokens);
          const isCountFunction = root.value === ARRAY_FUNCTION_ENUM.COUNT;

          functionTokens = [array, [getIteratorToken(array)], ...rest];

          if (isCountFunction && !rest.length) {
            functionTokens = [array];
          }
        }

        root.operands = functionTokens.reduce((acc, tokens) => {
          const tree = createTree(tokens, root);

          if (tree) {
            acc.push(tree);
          }

          return acc;
        }, []);

        if (root.value === SWITCH_FUNCTION_ENUM.CASE) {
          const secondOperandType = root.operands?.[1]?.typeOfValue;

          if (secondOperandType) {
            root.typeOfValue = secondOperandType;
          }
        }

        if (root.value === SWITCH_FUNCTION_ENUM.SWITCH) {
          const firstCaseTypeOfValue = root.operands[0]?.typeOfValue;

          if (firstCaseTypeOfValue) {
            root.typeOfValue = firstCaseTypeOfValue;
          }
        }

        if (!error && restTokens.length) {
          error = getError(restTokens[0], root);
        }
      }
    }

    if (LEX_TOKEN_TYPES.spec_field === root.type) {
      const lexicalParser = new LexicalParser(schemaParams);

      const functionExpression = root.value.match(/\w+\([\s\S]*\)/)[0];
      const extractedField = root.value.replace(/\w+\([\s\S]*\)\./, '');

      const functionTokens = lexicalParser.parse(functionExpression);
      const functionTree = createTree(functionTokens, root);

      const arrayToken = functionTokens.find(
        ({ type, typeOfValue }) =>
          type === LEX_TOKEN_TYPES.field && typeOfValue === LEX_TOKEN_TYPES.array,
      );

      const pathToRootArray = getPathToRootArray({
        entitiesSchema: schemaParams.entities,
        arrayToken,
      });

      root.type = LEX_TOKEN_TYPES.field;
      root.typeOfValue = schemaParams.entities[pathToRootArray]?.[extractedField];
      root.value = `${pathToRootArray}.${extractedField}`;
      root.operands = [functionTree];
      root.spec_field = true;
    }

    if (!isOnlyBooleanAllowed && root.type !== LEX_TOKEN_TYPES.function) {
      const isMultipleTokens = !parent && !root.operands && tokens.length > 1;

      if (!error && isMultipleTokens) {
        error = getError(tokens[1], root);
      }
    }

    return root;
  };

  const processedTokens = processTokens(tokens);

  const data = createTree(processedTokens);

  const { validationError, fields } = validateTree({
    schemaParams,
    data,
    isOnlyBooleanAllowed,
    isOnlyNumericAllowed,
  });

  if (isMinimized) {
    minimizeTokens(data);
  }

  return { data, fields, error: validationError ? { message: validationError } : error };
};

export default class SyntaxParser {
  constructor(schemaParams) {
    this.schemaParams = schemaParams;
    this.tokens = [];
    this.index = 0;
    this.error = null;
  }

  _processTokens(tokens) {
    const processedTokens = [];
    let index = 0;

    while (index < tokens.length) {
      if (
        tokens[index]?.value !== '-' ||
        (tokens[index]?.value === '-' &&
          (NUMERIC_TYPES.includes(tokens[index - 1]?.typeOfValue) ||
            tokens[index - 1]?.type === LEX_TOKEN_TYPES.bracket)) ||
        !tokens[index + 1]
      ) {
        processedTokens.push(tokens[index]);
      } else {
        index += 1;
        tokens[index].value = `-${tokens[index].value}`;
        processedTokens.push(tokens[index]);
      }
      index += 1;
    }

    return processedTokens;
  }

  parse({ tokens, isMinimized, isOnlyBooleanAllowed, isOnlyNumericAllowed }) {
    this.error = null;

    if (!tokens.length) {
      return {};
    }

    const logicalInfixError = findLogicalInfixOperatorError(tokens);

    if (logicalInfixError) {
      return { data: {}, fields: undefined, error: logicalInfixError };
    }

    const processedTokens = this._processTokens(tokens);

    this.tokens = processedTokens;
    this.index = 0;

    const data = this._createTree();

    const { validationError, fields } = validateTree({
      schemaParams: this.schemaParams,
      data,
      isOnlyBooleanAllowed,
      isOnlyNumericAllowed,
    });

    if (isMinimized) {
      minimizeTokens(data);
    }

    return { data, fields, error: validationError ? { message: validationError } : this.error };
  }

  // Simplified parse method for internal class instances use only
  parseInternal(tokens) {
    this.error = null;

    if (!tokens.length) {
      return {};
    }

    this.tokens = tokens;
    this.index = 0;

    return this._createTree();
  }

  _createTree(rbp = 0) {
    let token = this._next();
    let left = this._nud(token);

    while (
      this._peek() &&
      getTokenPriority(this._peek().value) &&
      getTokenPriority(this._peek().value) > rbp
    ) {
      token = this._next();
      left = this._led(left, token);
    }

    return left;
  }

  // NUD: null-denotation (prefix, literals, identifiers)
  _nud(token) {
    switch (token?.type) {
      case LEX_TOKEN_TYPES.value:
      case LEX_TOKEN_TYPES.field:
      case LEX_TOKEN_TYPES.date:
      case LEX_TOKEN_TYPES.bool:
      case LEX_TOKEN_TYPES.decimal:
        return {
          ...token,
        };

      case LEX_TOKEN_TYPES.function: {
        if (this.schemaParams.functions[token.value].argumentsCount === 0) {
          return {
            ...token,
            operands: [],
          };
        }

        if (this._peek() && this._peek().value === '(') {
          this._next(); // consume '('
          let args = [];

          if (this._peek().value !== ')') {
            do {
              args.push(this._createTree());
            } while (
              this._peek() &&
              this._peek().type === LEX_TOKEN_TYPES.separator &&
              this._next()
            );

            if (ARRAY_FUNCTIONS_WITH_ITERATORS.includes(token.value) && !_.isEmpty(args[0])) {
              const isCountFunction = token.value === ARRAY_FUNCTION_ENUM.COUNT;
              const [array, ...rest] = args;

              args = [array, getIteratorToken(null, array), ...rest];

              if (isCountFunction && !rest.length) {
                args = [array];
              }
            }

            if (token.value === SWITCH_FUNCTION_ENUM.CASE) {
              const secondOperandType = args?.[1]?.typeOfValue;

              if (secondOperandType) {
                token.typeOfValue = secondOperandType;
              }
            }

            if (token.value === SWITCH_FUNCTION_ENUM.SWITCH) {
              const firstCaseTypeOfValue = args?.[0]?.typeOfValue;

              if (firstCaseTypeOfValue) {
                token.typeOfValue = firstCaseTypeOfValue;
              }
            }
          }

          if (!this._peek() || this._peek().value !== ')') {
            this.error = getError(token, token.parent);
          }

          this._next(); // consume ')'

          args = args.filter(Boolean);

          const functionToken = {
            ...token,
            operands: args,
          };

          if (!this.error && !args.length) {
            this.error = getError(token, functionToken);
          }

          if (args.length) {
            for (const arg of args) {
              arg.parent = _.omit(functionToken, 'operands');
            }
          }

          return functionToken;
        }

        break;
      }

      case LEX_TOKEN_TYPES.spec_field: {
        const lexicalParser = new LexicalParser(this.schemaParams);
        const syntaxParser = new SyntaxParser(this.schemaParams);

        const functionExpression = token.value.match(/\w+\([\s\S]*\)/)[0];
        const extractedField = token.value.replace(/\w+\([\s\S]*\)\./, '');

        const functionTokens = lexicalParser.parse(functionExpression);
        const functionTree = syntaxParser.parseInternal(functionTokens);

        const arrayToken = functionTokens.find(
          ({ type, typeOfValue }) =>
            type === LEX_TOKEN_TYPES.field && typeOfValue === LEX_TOKEN_TYPES.array,
        );

        const pathToRootArray = getPathToRootArray({
          entitiesSchema: this.schemaParams.entities,
          arrayToken,
        });

        token.type = LEX_TOKEN_TYPES.field;
        token.typeOfValue = this.schemaParams.entities[pathToRootArray]?.[extractedField];
        token.value = `${pathToRootArray}.${extractedField}`;
        token.spec_field = true;

        if (functionTree.length) {
          for (const arg of functionTree) {
            arg.parent = _.omit(token, 'operands');
          }
        }

        token.operands = [functionTree];

        return token;
      }

      case LEX_TOKEN_TYPES.bracket: {
        if (token.value === '(') {
          const expression = this._createTree();
          this._next(); // consume ')'
          return expression;
        }

        break;
      }

      // Throw error if there is anySingleCharacter
      case LEX_TOKEN_TYPES.anySingleCharacter:
        if (!this.error) {
          this.error = getError(token, token.parent);
        }

        break;

      default:
        break;
    }
  }

  // LED: left-denotation (binary/postfix operators)
  _led(left, token) {
    const rbp = getTokenPriority(token?.value);

    if (!rbp) {
      if (!this.error) {
        this.error = getError(token, token?.parent);
      }

      return;
    }

    const operatorToken = {
      ...token,
      operands: [left, this._createTree(rbp)].filter(Boolean),
    };

    if (operatorToken.operands.length) {
      for (const arg of operatorToken.operands) {
        arg.parent = _.omit(operatorToken, 'operands');
      }
    }

    return operatorToken;
  }

  _next() {
    return this.tokens[this.index++];
  }

  _peek() {
    return this.tokens[this.index];
  }
}
